import { N } from './fancybox-BwIiSbEY.js';
import { P as PubSub } from './pubsub-CoPYzvVm.js';
import './notyf-DIw0RvBj.js';

class ProductMedia extends HTMLElement {
  #mediaItems = [...this.querySelectorAll('.product-media-item')];
  #thumbSwiper;
  #swiper;
  #unsubscribers = [];

  async connectedCallback() {
    await this.#initThumbSwiper();
    await this.#initSwiper();
    this.initFancybox();

    this.#unsubscribers.push(
      PubSub.subscribe('variant:change', ({ id, data: { html } }) => {
        if (id !== this.dataset.productId) return;
        this.#handleVariantChange(html);
      })
    );
  }

  disconnectedCallback() {
    this.#unsubscribers.forEach((unsubscribe) => unsubscribe());
  }

  /**
   * Get the index of the slide that matches the selected variant's featured media.
   * @returns {number} The index of the matching slide, or 0 if none found.
   */
  #getSelectedVariantSlideIndex() {
    const selectedItem = this.querySelector('.product-media-item[data-selected-variant-image]');
    if (!selectedItem) return 0;
    return Math.max(0, this.#mediaItems.indexOf(selectedItem));
  }

  async #initSwiper() {
    if (this.#mediaItems.length <= 1) return;

    try {
      const { Swiper } = await import('./swiper-D_0i_b49.js');
      const { Thumbs, Navigation, Pagination } = await import('./index-JM8sFIGx.js');

      this.#swiper = new Swiper('.swiper--media', {
        modules: [Thumbs, Navigation, Pagination],
        spaceBetween: 16,
        initialSlide: this.#getSelectedVariantSlideIndex(),
        navigation: {
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        },
        pagination: {
          el: '.swiper-pagination',
          clickable: true,
        },
        thumbs: {
          swiper: this.#thumbSwiper,
        },
      });
    } catch (error) {
      console.error('Error initializing Swiper:', error);
    }
  }

  /**
   * Handle a variant change by sliding to the variant's featured media.
   * Reads the freshly-rendered section HTML to find which media is now
   * marked as the selected variant image, then matches it to the existing
   * gallery slide by media ID.
   * @param {Document} html - The newly fetched section HTML.
   */
  #handleVariantChange(html) {
    if (!this.#swiper) return;

    const newSelectedItem = html.querySelector('.product-media-item[data-selected-variant-image]');
    const mediaId = newSelectedItem?.dataset.mediaId;
    if (!mediaId) return;

    const targetIndex = this.#mediaItems.findIndex((item) => item.dataset.mediaId === mediaId);
    if (targetIndex >= 0) this.#swiper.slideTo(targetIndex);
  }

  async #initThumbSwiper() {
    if (this.#mediaItems.length <= 1) return;

    try {
      const { Swiper } = await import('./swiper-D_0i_b49.js');

      this.#thumbSwiper = new Swiper('.swiper--thumbs', {
        spaceBetween: 8,
        slidesPerView: 'auto',
        watchSlidesProgress: true,
      });
    } catch (error) {
      console.error('Error initializing thumb Swiper:', error);
    }
  }

  initFancybox() {
    N.bind('[data-fancybox]', {
      Carousel: {
        Panzoom: {
          touch: (panzoom, event) => event?.target.nodeName !== 'MODEL-VIEWER',
        },
      },
    });
  }
}

customElements.define('product-media', ProductMedia);
